import { z } from 'zod';

// Browser-safe enumerations shared by web, api and worker.
// Values mirror prisma/schema.prisma enums exactly.

export const ROLES = ['owner', 'manager', 'viewer'] as const;
export const RoleSchema = z.enum(ROLES);
export type Role = z.infer<typeof RoleSchema>;

/** Role ordering used for "at least" checks. */
export const ROLE_RANK: Record<Role, number> = { viewer: 1, manager: 2, owner: 3 };
export function roleAtLeast(role: Role, required: Role): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[required];
}

export const CONNECTION_STATES = [
  'disconnected',
  'awaiting_qr',
  'connecting',
  'syncing',
  'connected',
  'reconnecting',
  'logged_out',
  'error',
  'paused',
] as const;
export const ConnectionStateSchema = z.enum(CONNECTION_STATES);
export type ConnectionState = z.infer<typeof ConnectionStateSchema>;

export const DESIRED_STATES = ['active', 'paused', 'unlinked'] as const;
export const DesiredStateSchema = z.enum(DESIRED_STATES);
export type DesiredState = z.infer<typeof DesiredStateSchema>;

export const ATTRIBUTION_MODES = ['assigned', 'shared'] as const;
export const AttributionModeSchema = z.enum(ATTRIBUTION_MODES);
export type AttributionMode = z.infer<typeof AttributionModeSchema>;

export const ATTRIBUTIONS = ['assigned', 'shared', 'unassigned'] as const;
export const AttributionSchema = z.enum(ATTRIBUTIONS);
export type Attribution = z.infer<typeof AttributionSchema>;

export const HISTORY_IMPORT_STATUSES = ['not_started', 'in_progress', 'completed', 'partial', 'failed'] as const;
export const HistoryImportStatusSchema = z.enum(HISTORY_IMPORT_STATUSES);
export type HistoryImportStatus = z.infer<typeof HistoryImportStatusSchema>;

export const GAP_REASONS = ['disconnected', 'history_incomplete', 'before_import', 'lease_lost', 'unknown'] as const;
export const GapReasonSchema = z.enum(GAP_REASONS);
export type GapReason = z.infer<typeof GapReasonSchema>;

export const ELIGIBILITIES = ['eligible', 'excluded'] as const;
export const EligibilitySchema = z.enum(ELIGIBILITIES);
export type Eligibility = z.infer<typeof EligibilitySchema>;

export const EXCLUSION_REASONS = ['personal', 'internal', 'spam', 'test', 'other'] as const;
export const ExclusionReasonSchema = z.enum(EXCLUSION_REASONS);
export type ExclusionReason = z.infer<typeof ExclusionReasonSchema>;

export const DIRECTIONS = ['inbound', 'outbound'] as const;
export const DirectionSchema = z.enum(DIRECTIONS);
export type Direction = z.infer<typeof DirectionSchema>;

export const MESSAGE_TYPES = [
  'text',
  'voice',
  'audio',
  'image',
  'video',
  'document',
  'sticker',
  'location',
  'contact',
  'other',
] as const;
export const MessageTypeSchema = z.enum(MESSAGE_TYPES);
export type MessageType = z.infer<typeof MessageTypeSchema>;

export const MEDIA_STATUSES = ['pending', 'stored', 'unavailable', 'too_large', 'skipped', 'deleted'] as const;
export const MediaStatusSchema = z.enum(MEDIA_STATUSES);
export type MediaStatus = z.infer<typeof MediaStatusSchema>;

export const TRANSCRIPT_STATUSES = ['pending', 'completed', 'failed', 'unavailable', 'disabled'] as const;
export const TranscriptStatusSchema = z.enum(TRANSCRIPT_STATUSES);
export type TranscriptStatus = z.infer<typeof TranscriptStatusSchema>;

export const EPISODE_OPENINGS = ['first_message', 'inactivity', 'manual_close'] as const;
export const EpisodeOpeningSchema = z.enum(EPISODE_OPENINGS);
export type EpisodeOpening = z.infer<typeof EpisodeOpeningSchema>;

export const TOUCH_KINDS = ['initial_outreach', 'initial_response', 'follow_up', 'active_response'] as const;
export const TouchKindSchema = z.enum(TOUCH_KINDS);
export type TouchKind = z.infer<typeof TouchKindSchema>;

export const RESPONSE_STATUSES = ['responded', 'pending', 'not_applicable', 'uncertain'] as const;
export const ResponseStatusSchema = z.enum(RESPONSE_STATUSES);
export type ResponseStatus = z.infer<typeof ResponseStatusSchema>;

export const ANALYSIS_STATUSES = [
  'pending',
  'running',
  'completed',
  'insufficient_evidence',
  'failed',
  'disabled',
  'budget_limited',
  'stale',
] as const;
export const AnalysisStatusSchema = z.enum(ANALYSIS_STATUSES);
export type AnalysisStatus = z.infer<typeof AnalysisStatusSchema>;

export const PRIMARY_MOTIVES = [
  'pricing',
  'availability',
  'information',
  'appointment',
  'financing',
  'purchase',
  'support',
  'other',
  'unknown',
] as const;
export const PrimaryMotiveSchema = z.enum(PRIMARY_MOTIVES);
export type PrimaryMotive = z.infer<typeof PrimaryMotiveSchema>;

export const INTENTS = ['exploring', 'comparing', 'ready_to_proceed', 'post_sale', 'unknown'] as const;
export const IntentSchema = z.enum(INTENTS);
export type Intent = z.infer<typeof IntentSchema>;

/** Metric polarity drives trend badge semantics: is an increase good, bad, or not inferable? */
export const METRIC_POLARITIES = ['higher_is_better', 'lower_is_better', 'neutral'] as const;
export type MetricPolarity = (typeof METRIC_POLARITIES)[number];
